
const libPictProvider = require('pict-provider');

// An ArtifactType is a reusable data-shape: a Name + a JSON-Schema `Schemata` whose `properties`
// define the columns/fields an observation requirement collects (a single value like Data_String,
// or a multi-column "row" like LegacyPersonnel). This editor lets you define those properties.
// ArtifactType is writable on the API (User: Create=Allow / Update=Mine, Administrator: Update=Allow),
// so creating is open to any user; editing an existing one needs ownership or admin.

const PROPERTY_TYPES = [ 'string', 'number', 'integer', 'boolean' ];

/**
 * ArtifactTypeEditor — the data layer behind the bespoke ArtifactType builder. Loads an artifact's
 * Schemata into an ordered property model the view edits, and persists it back as JSON Schema.
 */
class ArtifactTypeEditorProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;

		this._allArtifacts = null;
		this._editor = null;
		this._rowKeySeed = 1;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get currentUser() { return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {}; }
	get idUser() { return this.currentUser.UserID; }
	get isAdmin() { return Number(this.currentUser.UserRoleIndex) === 5; }

	/** Editable when creating new, when an admin, or when the current user created this artifact (Update=Mine). */
	_canEdit(pArtifact, pMode)
	{
		if (pMode === 'new') { return true; }
		if (this.isAdmin) { return true; }
		return Number(pArtifact && pArtifact.CreatingIDUser) === Number(this.idUser);
	}

	// --- REST helpers ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) => { this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody)); });
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) => { this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody)); });
	}

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) => { this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody)); });
	}

	async _ensureAllArtifactsAsync()
	{
		if (this._allArtifacts) { return this._allArtifacts; }
		let tmpRows = [];
		try { tmpRows = await this._getJSON('ArtifactTypes'); }
		catch (pError) { this.log.warn(`ArtifactTypeEditor: could not load the artifact catalog: ${pError.message || pError}`); }
		this._allArtifacts = Array.isArray(tmpRows) ? tmpRows : [];
		return this._allArtifacts;
	}

	_nameInUse(pName, pExcludeID)
	{
		const tmpName = (pName || '').trim().toLowerCase();
		return (this._allArtifacts || []).some((pArtifact) =>
			pArtifact && (pArtifact.Deleted !== 1) && ((pArtifact.Name || '').trim().toLowerCase() === tmpName)
			&& (Number(pArtifact.IDArtifactType) !== Number(pExcludeID)));
	}

	// --- Schemata <-> property-model ---

	/**
	 * Parse an artifact's Schemata JSON Schema into ordered, editable property rows. Keeps the raw
	 * property object on each row so unknown attributes survive a round-trip.
	 * @param {Object} pArtifact
	 * @return {Array<Object>}
	 */
	_propertiesFromSchemata(pArtifact)
	{
		let tmpSchema = pArtifact && pArtifact.Schemata;
		if (typeof tmpSchema === 'string') { try { tmpSchema = JSON.parse(tmpSchema); } catch (pError) { tmpSchema = {}; } }
		const tmpProperties = (tmpSchema && tmpSchema.properties) || {};
		const tmpRequired = Array.isArray(tmpSchema && tmpSchema.required) ? tmpSchema.required : [];
		return Object.keys(tmpProperties)
			.map((pKey) => Object.assign({ Key: pKey }, tmpProperties[pKey]))
			.sort((pA, pB) => ((pA.ordinal == null ? 999 : pA.ordinal) - (pB.ordinal == null ? 999 : pB.ordinal)))
			.map((pProperty) =>
				(
					{
						_key: this._rowKeySeed++,
						_original: pProperty,
						Key: pProperty.Key,
						DisplayName: pProperty.displayName || '',
						Type: (PROPERTY_TYPES.indexOf((`${pProperty.type || 'string'}`).toLowerCase()) >= 0) ? (`${pProperty.type}`).toLowerCase() : 'string',
						EnumOptions: Array.isArray(pProperty.enumOptions) ? pProperty.enumOptions.join(', ') : '',
						Required: (tmpRequired.indexOf(pProperty.Key) >= 0),
						Hidden: (pProperty.visible === false) || (pProperty.Hidden === true)
					}));
	}

	/**
	 * Serialize the working property model back into a JSON Schema, preserving unknown attributes.
	 * @param {String} pTitle
	 * @return {Object}
	 */
	_buildSchemata(pTitle)
	{
		const tmpProperties = {};
		const tmpRequired = [];
		this._editor.properties.forEach((pRow, pIndex) =>
		{
			const tmpKey = (pRow.Key || '').trim();
			if (!tmpKey) { return; }
			const tmpProperty = Object.assign({}, pRow._original || {});
			tmpProperty.type = pRow.Type;
			tmpProperty.ordinal = pIndex;
			if ((pRow.DisplayName || '').trim()) { tmpProperty.displayName = pRow.DisplayName.trim(); }
			else { delete tmpProperty.displayName; }
			const tmpEnum = (pRow.EnumOptions || '').split(',').map((pOption) => pOption.trim()).filter((pOption) => pOption.length > 0);
			if (tmpEnum.length > 0) { tmpProperty.enumOptions = tmpEnum; }
			else { delete tmpProperty.enumOptions; }
			if (pRow.Hidden) { tmpProperty.visible = false; }
			else { delete tmpProperty.visible; delete tmpProperty.Hidden; }
			delete tmpProperty.Key;
			tmpProperties[tmpKey] = tmpProperty;
			if (pRow.Required) { tmpRequired.push(tmpKey); }
		});
		return { title: pTitle, type: 'object', properties: tmpProperties, required: tmpRequired };
	}

	_blankProperty()
	{
		return { _key: this._rowKeySeed++, _original: {}, Key: '', DisplayName: '', Type: 'string', EnumOptions: '', Required: false, Hidden: false };
	}

	// --- Load / new / save ---

	async loadAsync(pIDArtifact)
	{
		const tmpState = this.editorState;
		tmpState.Loading = true;
		tmpState.Message = '';
		this._render();
		try
		{
			await this._ensureAllArtifactsAsync();
			const tmpArtifact = await this._getJSON(`ArtifactType/${pIDArtifact}`);
			if (!tmpArtifact || !tmpArtifact.IDArtifactType) { throw new Error(`Artifact type ${pIDArtifact} not found.`); }
			this._editor =
			{
				mode: this._canEdit(tmpArtifact, 'edit') ? 'edit' : 'view',
				artifact: tmpArtifact,
				properties: this._propertiesFromSchemata(tmpArtifact)
			};
			tmpState.Loading = false;
			this._render();
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not load the artifact type: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	async newType()
	{
		const tmpState = this.editorState;
		tmpState.Loading = true;
		this._render();
		await this._ensureAllArtifactsAsync();
		this._editor = { mode: 'new', artifact: { Name: '' }, properties: [ this._blankProperty() ] };
		tmpState.Loading = false;
		this._render();
	}

	async openByGUIDAsync(pGUID)
	{
		const tmpState = this.editorState;
		tmpState.Loading = true; tmpState.Message = '';
		this._render();
		try
		{
			const tmpRows = await this._getJSON(`ArtifactTypes/FilteredTo/FBV~GUIDArtifactType~EQ~${pGUID}`);
			const tmpRecord = (Array.isArray(tmpRows) && tmpRows.length > 0) ? tmpRows[0] : null;
			if (!tmpRecord) { throw new Error(`No artifact type found for GUID ${pGUID}.`); }
			await this.loadAsync(tmpRecord.IDArtifactType);
		}
		catch (pError)
		{
			tmpState.Loading = false;
			tmpState.Message = `Could not open the artifact type: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	async saveAsync()
	{
		if (!this._editor) { return; }
		const tmpState = this.editorState;
		const tmpName = (this._editor.artifact.Name || '').trim();
		if (!tmpName)
		{
			tmpState.Message = 'A Name is required.'; tmpState.MessageClass = 'is-warning'; this._render(); return;
		}
		if (this._nameInUse(tmpName, this._editor.artifact.IDArtifactType))
		{
			tmpState.Message = `The name “${tmpName}” is already used by another artifact type.`; tmpState.MessageClass = 'is-warning'; this._render(); return;
		}
		const tmpKeys = this._editor.properties.map((pRow) => (pRow.Key || '').trim()).filter((pKey) => pKey.length > 0);
		if (tmpKeys.length === 0)
		{
			tmpState.Message = 'Add at least one field (a property with a key).'; tmpState.MessageClass = 'is-warning'; this._render(); return;
		}
		if ((new Set(tmpKeys)).size !== tmpKeys.length)
		{
			tmpState.Message = 'Field keys must be unique.'; tmpState.MessageClass = 'is-warning'; this._render(); return;
		}

		tmpState.Saving = true; tmpState.Message = '';
		this._render();
		try
		{
			const tmpRecord = Object.assign({}, this._editor.artifact, { Name: tmpName, Schemata: JSON.stringify(this._buildSchemata(tmpName)) });
			let tmpSaved;
			if (tmpRecord.IDArtifactType) { tmpSaved = await this._putJSON('ArtifactType', tmpRecord); }
			else { tmpSaved = await this._postJSON('ArtifactType', tmpRecord); }
			const tmpID = (tmpSaved && tmpSaved.IDArtifactType) || tmpRecord.IDArtifactType;
			this._allArtifacts = null;
			tmpState.Saving = false;
			await this.loadAsync(tmpID);
			this.editorState.Message = `Saved “${tmpName}”.`;
			this.editorState.MessageClass = 'is-success';
			this._render();
			if (typeof window !== 'undefined' && window.location) { window.location.hash = `#/ArtifactType/${tmpID}`; }
		}
		catch (pError)
		{
			tmpState.Saving = false;
			tmpState.Message = `Save failed: ${pError.message || pError}`;
			tmpState.MessageClass = 'is-danger';
			this._render();
		}
	}

	// --- Property-model mutations ---

	_row(pKey) { return this._editor ? this._editor.properties.find((pRow) => pRow._key === Number(pKey)) : null; }

	setArtifactName(pValue) { if (this._editor) { this._editor.artifact.Name = pValue; } }

	addProperty()
	{
		if (!this._editor) { return; }
		this._editor.properties.push(this._blankProperty());
		this._render();
	}

	removeProperty(pKey)
	{
		if (!this._editor) { return; }
		this._editor.properties = this._editor.properties.filter((pRow) => pRow._key !== Number(pKey));
		this._render();
	}

	setPropertyField(pKey, pField, pValue)
	{
		const tmpRow = this._row(pKey);
		if (!tmpRow) { return; }
		if ((pField === 'Required') || (pField === 'Hidden')) { tmpRow[pField] = !!pValue; }
		else { tmpRow[pField] = pValue; }
		if (pField === 'Type') { this._render(); }
	}

	_move(pKey, pDelta)
	{
		if (!this._editor) { return; }
		const tmpProperties = this._editor.properties;
		const tmpIndex = tmpProperties.findIndex((pRow) => pRow._key === Number(pKey));
		const tmpTarget = tmpIndex + pDelta;
		if (tmpIndex < 0 || tmpTarget < 0 || tmpTarget >= tmpProperties.length) { return; }
		const tmpMoved = tmpProperties.splice(tmpIndex, 1)[0];
		tmpProperties.splice(tmpTarget, 0, tmpMoved);
		this._render();
	}

	moveUp(pKey) { this._move(pKey, -1); }
	moveDown(pKey) { this._move(pKey, 1); }

	backToList()
	{
		if (typeof window !== 'undefined' && window.location) { window.location.hash = '#/PSRS/ArtifactType/List'; }
	}

	// --- Render-state ---

	get editorState()
	{
		if (!this.pict.AppData.ArtifactTypeEditor)
		{
			this.pict.AppData.ArtifactTypeEditor =
			{
				Loading: false, Saving: false, Message: '', MessageClass: '',
				LoadingSlot: [], EditorSlot: [], EmptySlot: [ {} ], MessageSlot: [], ReadOnlyNoticeSlot: [],
				Mode: '', CanEdit: false, Name: '', TitleText: 'Artifact Type',
				Properties: [], PropertyCount: 0, TypeOptions: PROPERTY_TYPES.slice(),
				SaveDisabledAttr: '', ReadOnlyAttr: ''
			};
		}
		return this.pict.AppData.ArtifactTypeEditor;
	}

	deriveRenderState()
	{
		const tmpState = this.editorState;
		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message, MessageClass: tmpState.MessageClass || 'is-info' } ] : [];

		if (tmpState.Loading) { tmpState.LoadingSlot = [ {} ]; tmpState.EditorSlot = []; tmpState.EmptySlot = []; return; }
		if (!this._editor) { tmpState.LoadingSlot = []; tmpState.EditorSlot = []; tmpState.EmptySlot = [ {} ]; return; }

		const tmpCanEdit = (this._editor.mode === 'new') || (this._editor.mode === 'edit');
		tmpState.Mode = this._editor.mode;
		tmpState.CanEdit = tmpCanEdit;
		tmpState.ReadOnlyAttr = tmpCanEdit ? '' : 'disabled';
		tmpState.Name = this._editor.artifact.Name || '';
		tmpState.TitleText = (this._editor.mode === 'new') ? 'New Artifact Type' : (this._editor.artifact.Name || 'Artifact Type');
		tmpState.ReadOnlyNoticeSlot = tmpCanEdit ? [] : [ {} ];

		tmpState.Properties = this._editor.properties.map((pRow) =>
		{
			const tmpIsString = (pRow.Type === 'string');
			return (
				{
					Key: pRow._key,
					KeyName: pRow.Key || '',
					DisplayName: pRow.DisplayName || '',
					EnumOptions: pRow.EnumOptions || '',
					RequiredAttr: pRow.Required ? 'checked' : '',
					HiddenAttr: pRow.Hidden ? 'checked' : '',
					ReadOnlyAttr: tmpCanEdit ? '' : 'disabled',
					EnumDisabledAttr: (tmpCanEdit && tmpIsString) ? '' : 'disabled',
					TypeOptions: PROPERTY_TYPES.map((pType) => ({ Value: pType, SelectedAttr: (pType === pRow.Type) ? 'selected' : '' }))
				});
		});
		tmpState.PropertyCount = this._editor.properties.length;
		tmpState.EmptyPropertiesSlot = (this._editor.properties.length > 0) ? [] : [ {} ];
		tmpState.SaveDisabledAttr = (tmpState.Saving || !tmpCanEdit) ? 'disabled' : '';
		tmpState.ShowSave = tmpCanEdit;
		tmpState.LoadingSlot = []; tmpState.EmptySlot = [];
		tmpState.EditorSlot = [ tmpState ];
	}

	_render()
	{
		this.deriveRenderState();
		if (this.pict.views.ArtifactTypeEditor) { this.pict.views.ArtifactTypeEditor.render(); }
	}
}

module.exports = ArtifactTypeEditorProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ArtifactTypeEditor',
	AutoInitialize: true,
	AutoInitializeOrdinal: 211
};
